import { AxiosError } from 'axios';

import { SerializedApiError } from '../typings/global';
import { ServerError } from './statusCode';

export interface BaseErrorResponse {
  statusCode?: number;
  status?: number;
  name?: string;
  message?: string;
  code?: string;
  details?: any;
  error?: string;
  timestamp?: string;
  path?: string;
}

export function createSerializedApiError(error: any): SerializedApiError {
  // Função auxiliar para extrair mensagem de forma segura
  const extractMessage = (data: any): string => {
    if (!data) return 'Erro desconhecido';

    // Tenta extrair mensagem de diferentes estruturas
    if (typeof data.message === 'string') return data.message;
    if (typeof data.details?.message === 'string') return data.details.message;
    if (typeof data.details?.error === 'string') return data.details.error;
    if (typeof data.error === 'string') return data.error;

    // Se details.message for array, faz join
    if (Array.isArray(data.details?.message)) {
      return data.details.message.join(', ');
    }

    // Se message for array, faz join
    if (Array.isArray(data.message)) {
      return data.message.join(', ');
    }

    // Fallback: tenta stringify se for objeto
    if (typeof data === 'object') {
      try {
        return JSON.stringify(data);
      } catch {
        return 'Erro na resposta do servidor';
      }
    }

    return String(data);
  };

  // Função auxiliar para extrair status code de forma segura
  const extractStatusCode = (
    data: any,
    defaultStatus: number = ServerError
  ): number => {
    return data?.statusCode || data?.status || defaultStatus;
  };

  if (isAxiosError(error)) {
    const isNetworkError = !!error.isAxiosError && !error.response;

    if (isNetworkError) {
      return {
        isSerializedApiError: true,
        status: 500,
        name: 'NetworkError',
        message: 'Erro de conexão com o servidor',
        code: 'NETWORK_ERROR',
      };
    }

    const response = error.response?.data;
    const status = extractStatusCode(response, error.response?.status);
    const message = extractMessage(response);

    return {
      isSerializedApiError: true,
      status,
      name: response?.name || response?.error || 'ApiError',
      message,
      code: response?.code,
    };
  }

  // Para erros que não são do Axios
  if (error instanceof Error) {
    return {
      isSerializedApiError: true,
      status: ServerError,
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  // Para qualquer outro tipo de erro
  return {
    isSerializedApiError: true,
    status: ServerError,
    name: 'UnknownError',
    message: extractMessage(error),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isAxiosError(e: any): e is AxiosError<BaseErrorResponse> {
  return e && typeof e === 'object' && e.isAxiosError === true;
}
